import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useWebSocketStatus, useWebSocketContext } from "../contexts/WebSocketContext";
import { SessionLayout } from "../components/session/SessionLayout";
import { AudioSettingsPanel } from "../components/session/AudioSettingsPanel";
import { useVoiceChat } from "../hooks/useVoiceChat";
import { apiFetch } from "../services/apiClient";

interface SessionPageProps {
  sessionId: string;
  userId: string;
  characterId?: string;
  characterName?: string;
  isDM?: boolean;
  campaignId?: number;
  campaignCreatedBy?: string;
}

export const SessionPage: React.FC<SessionPageProps> = ({
  sessionId,
  userId,
  characterId,
  characterName,
  isDM = false,
  campaignId,
  campaignCreatedBy,
}) => {
  const navigate = useNavigate();
  const { isConnected, reconnectAttempts } = useWebSocketStatus();
  const { joinSession, leaveSession, websocketService } = useWebSocketContext();
  const [theme, setTheme] = useState<string>("dark");
  const [audioSettingsOpen, setAudioSettingsOpen] = useState(false);

  // Initialize voice chat
  const {
    isEnabled: voiceEnabled,
    isMuted: voiceMuted,
    volume: voiceVolume,
    settings: audioSettings,
    availableInputDevices,
    availableOutputDevices,
    toggleEnabled: handleToggleVoice,
    toggleMute: handleToggleMute,
    setVolume: handleVolumeChange,
    updateSettings: handleUpdateSettings,
    cleanup: cleanupVoice,
  } = useVoiceChat(sessionId, userId, websocketService);

  // Fetch session details including theme
  useEffect(() => {
    const fetchSessionDetails = async () => {
      try {
        const response = await apiFetch(`/api/protected/sessions/${sessionId}`);

        if (response.ok) {
          const data = await response.json();
          if (data.theme) {
            setTheme(data.theme);
          }
        }
      } catch (error) {
        console.error("Failed to fetch session details:", error);
      }
    };

    fetchSessionDetails();
  }, [sessionId]);

  // Join the session when component mounts or when connection is re-established
  useEffect(() => {
    if (isConnected && sessionId) {
      // Add a small delay to ensure WebSocket is fully ready
      const timeoutId = setTimeout(() => {
        const success = joinSession(sessionId, characterId, characterName);
        if (!success) {
          console.error(`❌ Failed to join session ${sessionId}`);
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isConnected, sessionId, characterId, characterName, reconnectAttempts, joinSession]);

  // Cleanup: leave session when component unmounts
  useEffect(() => {
    return () => {
      if (sessionId) {
        leaveSession(sessionId);
      }
      cleanupVoice();
    };
  }, [sessionId, leaveSession, cleanupVoice]);

  // Explicit "Leave Session" — called from the bottom bar button
  const handleLeaveSession = useCallback(() => {
    leaveSession(sessionId);
    cleanupVoice();
    navigate("/protected/home");
  }, [sessionId, leaveSession, cleanupVoice, navigate]);

  return (
    <div className="fixed inset-0 bg-gray-900 text-white overflow-hidden">
      <SessionLayout
        sessionId={sessionId}
        userId={userId}
        characterId={characterId}
        isDM={isDM}
        isConnected={isConnected}
        reconnectAttempts={reconnectAttempts}
        campaignId={campaignId}
        campaignCreatedBy={campaignCreatedBy}
        theme={theme}
        voiceEnabled={voiceEnabled}
        voiceMuted={voiceMuted}
        voiceVolume={voiceVolume}
        onToggleVoice={handleToggleVoice}
        onToggleMute={handleToggleMute}
        onVolumeChange={handleVolumeChange}
        onOpenAudioSettings={() => setAudioSettingsOpen(true)}
        onLeaveSession={handleLeaveSession}
      />

      <AudioSettingsPanel
        isOpen={audioSettingsOpen}
        onClose={() => setAudioSettingsOpen(false)}
        settings={audioSettings}
        onSettingsChange={handleUpdateSettings}
        availableInputDevices={availableInputDevices}
        availableOutputDevices={availableOutputDevices}
      />
    </div>
  );
};
